import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, Circle, MinusCircle, Compass, MessageSquarePlus, StickyNote, FileText } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");
const fmtDT = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const roleLabel = { listing_agent: "Listing Agent", buyer_agent: "Buyer Agent", listing_coordinator: "Listing Coordinator", buyer_coordinator: "Buyer Coordinator", guest: "" };

function NoteForm({ token, taskId, onDone }) {
  const qc = useQueryClient();
  const [name, setName] = useState(localStorage.getItem("guest_name") || "");
  const [text, setText] = useState("");
  const add = useMutation({
    mutationFn: () => api.post(`/share/${token}/notes`, { name, text, task_id: taskId || null }).then((r) => r.data),
    onSuccess: () => { localStorage.setItem("guest_name", name); setText(""); qc.invalidateQueries({ queryKey: ["share", token] }); toast.success("Note sent to the agent"); onDone && onDone(); },
    onError: (e) => toast.error(formatError(e.response?.data?.detail)),
  });
  return (
    <div className="space-y-3">
      <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Your name</Label>
        <Input data-testid="share-note-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-card" placeholder="Jane Doe" /></div>
      <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Note or reminder</Label>
        <Textarea data-testid="share-note-text" value={text} onChange={(e) => setText(e.target.value)} className="mt-1.5 bg-card" placeholder="Ask a question or leave a reminder…" /></div>
      <Button data-testid="share-note-submit" disabled={!name.trim() || !text.trim() || add.isPending} onClick={() => add.mutate()}
        className="bg-primary text-primary-foreground rounded-full w-full">{add.isPending ? "Sending…" : "Send note"}</Button>
    </div>
  );
}

export default function ClientView() {
  const { token } = useParams();
  const { data, isLoading, isError } = useQuery({ queryKey: ["share", token], queryFn: () => api.get(`/share/${token}`).then((r) => r.data), retry: false });

  if (isLoading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (isError || !data) return <div className="min-h-screen grid place-items-center text-muted-foreground">This share link is no longer available.</div>;

  const { deal, tasks, phases, notes = [] } = data;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-8 h-16 flex items-center gap-2.5">
          <img src="/equitas-logo.png" alt="Equitas" className="w-8 h-8 rounded-full" />
          <span className="font-heading text-xl text-primary">Unified Transaction Hub</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <p className="eyebrow text-accent mb-3">Your transaction · guided by {deal.listing_agent_name}</p>
        <h1 className="text-4xl sm:text-5xl text-foreground">{deal.title}</h1>
        <p className="text-muted-foreground mt-1">{deal.address}</p>
        {(deal.buyer_names || deal.seller_names) && (
          <p className="text-sm text-muted-foreground mt-1.5" data-testid="share-parties">
            {deal.buyer_names && <span>Buyer: <span className="text-foreground font-medium">{deal.buyer_names}</span></span>}
            {deal.buyer_names && deal.seller_names && <span className="mx-2">·</span>}
            {deal.seller_names && <span>Seller: <span className="text-foreground font-medium">{deal.seller_names}</span></span>}
          </p>
        )}
        <p className="text-sm text-muted-foreground mt-3 max-w-xl">A transparent view into every step to the closing table. Have a question about any step? Tap <span className="text-primary font-medium">Leave a note</span> and your agent will get it directly.</p>

        <div className="mt-8 bg-card border border-border rounded-md overflow-hidden">
          <img src={deal.cover_image} alt="" className="w-full h-56 object-cover" />
          <div className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="font-heading text-xl text-foreground">{deal.current_phase}</span>
              <span className="text-sm text-muted-foreground">{deal.completed_tasks}/{deal.total_tasks} milestones · {deal.progress}%</span>
            </div>
            <Progress value={deal.progress} className="h-2" />
            {(deal.disclosure_link || deal.escrow_officer || deal.has_contract || deal.mls_url) && (
              <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                {deal.disclosure_link && (
                  <a href={deal.disclosure_link} target="_blank" rel="noopener noreferrer" data-testid="share-disclosure-link" className="text-primary flex items-center gap-1.5 hover:underline font-medium"><FileText className="w-4 h-4" /> View disclosure package</a>
                )}
                {deal.mls_url && (
                  <a href={deal.mls_url} target="_blank" rel="noopener noreferrer" data-testid="share-mls-link" className="text-primary flex items-center gap-1.5 hover:underline font-medium"><FileText className="w-4 h-4" /> View MLS printout</a>
                )}
                {deal.has_contract && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/share/${token}/contract`} target="_blank" rel="noopener noreferrer" data-testid="share-rpa-link" className="text-primary flex items-center gap-1.5 hover:underline font-medium"><FileText className="w-4 h-4" /> View purchase agreement (RPA)</a>
                )}
                {deal.escrow_officer && (
                  <span className="text-muted-foreground">Escrow: <span className="text-foreground">{deal.escrow_officer}</span>{deal.escrow_company ? ` · ${deal.escrow_company}` : ""}</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-10 space-y-8">
          {phases.map((phase) => {
            const items = tasks.filter((t) => t.phase === phase);
            if (!items.length) return null;
            const done = items.every((t) => t.completed || t.not_ordered);
            return (
              <div key={phase}>
                <h3 className={`font-heading text-2xl mb-3 flex items-center gap-2 ${done ? "text-primary" : "text-foreground"}`}>
                  {phase} {done && <CheckCircle2 className="w-5 h-5 text-primary" />}
                </h3>
                <div className="space-y-3 pl-1">
                  {items.map((t) => (
                    <div key={t.id} className="border-t border-border/60 pt-3" data-testid={`share-task-${t.id}`}>
                      <div className="flex items-start gap-3 text-sm">
                        {t.completed ? <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" /> : t.not_ordered ? <MinusCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" /> : <Circle className="w-4 h-4 text-border shrink-0 mt-0.5" />}
                        <div className="flex-1 min-w-0">
                          <span className={t.completed || t.not_ordered ? "text-muted-foreground line-through" : "text-foreground"}>{t.title}</span>
                          {t.completed && t.completed_by && <span className="text-xs text-muted-foreground ml-2">Completed by {t.completed_by}</span>}
                          {t.not_ordered && <span className="text-xs text-amber-700 ml-2">Not ordered / buyer declined{t.not_ordered_reason ? ` — ${t.not_ordered_reason}` : ""}</span>}
                          {t.notes.length > 0 && (
                            <div className="mt-2 space-y-1.5">
                              {t.notes.map((n, i) => (
                                <div key={i} className="text-xs bg-muted/50 rounded px-2.5 py-1.5">
                                  <span className="text-foreground">{n.text}</span>
                                  <span className="text-muted-foreground"> — {n.author_name}{roleLabel[n.author_role] ? ` (${roleLabel[n.author_role]})` : ""}, {fmtDT(n.created_at)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {t.due_date && <span className="text-xs text-muted-foreground">{fmtDate(t.due_date)}</span>}
                          <Dialog>
                            <DialogTrigger asChild>
                              <button data-testid={`share-leave-note-${t.id}`} className="text-xs text-primary hover:underline flex items-center gap-1"><MessageSquarePlus className="w-3.5 h-3.5" /> Leave a note</button>
                            </DialogTrigger>
                            <DialogContent className="bg-card">
                              <DialogHeader><DialogTitle className="font-heading text-xl">{t.title}</DialogTitle></DialogHeader>
                              <NoteForm token={token} taskId={t.id} />
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Workflow notes log */}
        <div className="mt-14 border-t border-border pt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-3xl text-foreground flex items-center gap-2"><StickyNote className="w-5 h-5 text-accent" /> Notes</h2>
            <Dialog>
              <DialogTrigger asChild>
                <Button data-testid="share-add-note-button" className="bg-primary text-primary-foreground rounded-full gap-2"><MessageSquarePlus className="w-4 h-4" /> Leave a note</Button>
              </DialogTrigger>
              <DialogContent className="bg-card">
                <DialogHeader><DialogTitle className="font-heading text-xl">Leave a note or reminder</DialogTitle></DialogHeader>
                <NoteForm token={token} />
              </DialogContent>
            </Dialog>
          </div>
          <p className="text-sm text-muted-foreground mb-5">Context, updates and off-checklist observations logged along the way.</p>
          <div className="space-y-5">
            {notes.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
            {notes.map((n, i) => (
              <div key={i} className="border-l-2 border-accent/40 pl-4" data-testid={`share-note-${i}`}>
                <p className="text-sm text-foreground leading-relaxed">{n.text}</p>
                <p className="text-xs text-muted-foreground mt-1">{n.author_name}{roleLabel[n.author_role] ? ` · ${roleLabel[n.author_role]}` : ""} · {fmtDT(n.created_at)}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-16">Anyone with this private link can view progress and leave notes for the agents.</p>
      </main>
    </div>
  );
}
